import { Button, Col, Flex, Form, Input, Row, Space, Typography } from "antd"
import FormattedMessage from "components/common/FormattedMessage";
import { ReactNode, useState, useEffect } from "react";
import { useRequest } from "standalone/hooks/useRequest";
import { useIntl } from "react-intl";
import { ReviewData, UserReviewSubmitData } from "./Review.types";
import Spinner from "components/common/Spinner";
import ReviewButtons from "./ReviewButtons";

const API_URL = import.meta.env.VITE_API_URL;


export const NumberOfStarsToText = (stars: number) => {
    switch (stars) {
        case 1:
            return "ONE"
        case 2:
            return "TWO"
        case 3:
            return "THREE"
        case 4:
            return "FOUR"
        case 5:
            return "FIVE"
        default:
            return "ONE"
    }
}

type ReviewFormProps = {
    message?: ReactNode,
    back?: boolean,
    onBack?: () => void,
    onSubmit?: (v: any) => void,
    reviewData: ReviewData,
    submitData: UserReviewSubmitData,
    showButtons?: boolean
}

const ReviewForm = (props: ReviewFormProps) => {

    const [submitted, setSubmitted] = useState(false);
    const feedbackCall = useRequest({});
    const intl = useIntl();
    const [form] = Form.useForm();
    const formName = Form.useWatch("name", form);
    const formReview = Form.useWatch("review", form);


    // Debug command to fill form with example data
    useEffect(() => {
        (window as any).debugFillReviewForm = () => {
            form.setFieldsValue({
                name: "John Doe",
                email: "john.doe@example.com",
                phone: "+1 (555) 123-4567",
                review: "This is an example review for testing purposes. The service was excellent and I would highly recommend it to others."
            });
            console.log("ReviewForm filled with debug data");
        };

        return () => {
            delete (window as any).debugFillReviewForm;
        };
    }, [form]);

    const handleSubmit = (v: any) => {
        console.log("submit", v);
        setSubmitted(true);
        return feedbackCall.call(() => {
            return fetch(`${API_URL}v1/reviews/${props.reviewData?.id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...v,
                    stars: NumberOfStarsToText(props.submitData.stars || 0)
                })
            })
        }).then(res => {
            if (res.ok) {
                props.onSubmit?.(v);
                return true;
            }
            return false;
        })
    }

    const handleButtonClick = async (platform: string) => {
        const values = form.getFieldsValue();
        const success = await handleSubmit(values);
        
        if (success) {
            const reviewText = values.review || props.submitData.review || "";
            navigator.clipboard.writeText(reviewText);

            const getLink = () => {
                switch (platform) {
                    case "Google":
                        return props.reviewData.google_button_link || "https://google.com";
                    case "Booksy":
                        return props.reviewData.booksy_button_link || "https://booksy.com";
                }
            }

            if (window.self === window.top) {
                window.location.href = getLink() || "";
            } else {
                window.parent.postMessage({
                    type: "ftRedirect",
                    link: getLink() || ""
                }, "*");
            }
        }
    }

    return (
        <Row style={{ maxWidth: "600px", padding: "var(--ant-padding-sm)" }}>
            <Col span={24}>
                {props.message ||
                    <FormattedMessage
                        id="standalone.reviews.feedbackFormBadRatingMessage"
                        defaultMessage="We want our Clients to be 100% satisfied. Let us know why you had a bad experience with our company. This will help us improve our services. Leave your email for contact!" />
                }
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Row>
                        <Col span={24}>
                            <Form.Item
                                name="name"
                            >
                                <Input placeholder={intl.formatMessage({
                                    id: "standalone.reviews.feedback.name.placeholder",
                                    defaultMessage: "Name",
                                    description: "Placeholder dla pola imienia w formularzu opinii"
                                })} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="email"
                                rules={[
                                    { type: 'email', message: 'Please enter a valid email!' }
                                ]}
                            >
                                <Input placeholder={intl.formatMessage({
                                    id: "standalone.reviews.feedback.email.placeholder",
                                    defaultMessage: "Email",
                                    description: "Placeholder dla pola email w formularzu opinii"
                                })} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                            >
                                <Input
                                    placeholder={intl.formatMessage({
                                        id: "standalone.reviews.feedback.phone.placeholder",
                                        defaultMessage: "Phone Number",
                                        description: "Placeholder dla pola numeru telefonu w formularzu opinii"
                                    })}
                                    onChange={(e) => {
                                        // Filter out invalid characters, keeping only numbers, +, #, (), spaces, and hyphens
                                        const filteredValue = e.target.value.replace(/[^0-9+#()\s-]/g, '');
                                        e.target.value = filteredValue;
                                        form.setFieldValue('phone', filteredValue);
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={24}>
                            <Form.Item
                                name="review"
                                initialValue={props.submitData.review || ""}
                            >
                                <Input.TextArea rows={4} placeholder={intl.formatMessage({
                                    id: "standalone.reviews.feedback.feedback.placeholder",
                                    defaultMessage: "Feedback",
                                    description: "Placeholder dla pola opinii w formularzu opinii"
                                })} />
                            </Form.Item>
                        </Col>
                    </Row>
                    {!props.showButtons ? (
                        <Row>
                            <Col span={24}>
                                <Flex justify="center" gap="small">
                                    <Form.Item>
                                        {props.back !== false && <Button
                                            onClick={props.onBack}
                                            disabled={feedbackCall.isLoading}
                                        >
                                            <FormattedMessage
                                                id="standalone.reviews.feedback.back"
                                                defaultMessage="Back" />
                                        </Button>}
                                    </Form.Item>
                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={feedbackCall.isLoading}
                                            disabled={!formName?.trim() && !formReview?.trim()}
                                        >
                                            <FormattedMessage
                                                id="standalone.reviews.feedback.submit"
                                                defaultMessage="Submit" />
                                        </Button>
                                    </Form.Item>
                                </Flex>
                            </Col>
                        </Row>
                    ) : (
                        <Row>
                            <Col span={24}>
                                <Flex justify="center" gap="small">
                                    {props.back !== false && (
                                        <Form.Item>
                                            <Button
                                                onClick={props.onBack}
                                                disabled={feedbackCall.isLoading}
                                            >
                                                <FormattedMessage
                                                    id="standalone.reviews.feedback.back"
                                                    defaultMessage="Back" />
                                            </Button>
                                        </Form.Item>
                                    )}
                                </Flex>
                            </Col>
                        </Row>
                    )}
                </Form>
                {props.showButtons && (
                    <Row>
                        <Col span={24}>
                            <ReviewButtons
                                reviewData={props.reviewData}
                                submitData={{
                                    ...props.submitData,
                                    review: formReview || props.submitData.review || ""
                                }}
                                onButtonClick={handleButtonClick}
                                isLoading={feedbackCall.isLoading}
                                disabled={!formName?.trim() && !formReview?.trim()}
                            />
                        </Col>
                    </Row>
                )}
            </Col>
        </Row>
    )
}

export default ReviewForm;